import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { handleMobileInput, handleGSTInput, validateMobile, validateGST } from '@/utils/inputValidation';
import { determineGstType, getEntityState, getEntityGstin } from '@/utils/gstUtils';

const BILLING_CYCLE_MIN_DAYS = { monthly: 30, quarterly: 90, half_yearly: 180, yearly: 365, two_years: 730, three_years: 1095 };
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { UserPlus, Users, FileText, Ban, Check, AlertTriangle, Share2, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BILLING_CYCLES = [
  { key: 'monthly', label: '1 Month' },
  { key: 'quarterly', label: '3 Months' },
  { key: 'half_yearly', label: '6 Months' },
  { key: 'yearly', label: '12 Months' },
  { key: 'two_years', label: '24 Months' },
  { key: 'three_years', label: '36 Months' }
];

export default function ResellerClients() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [subResellers, setSubResellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addClientDialogOpen, setAddClientDialogOpen] = useState(false);
  const [addSubResellerDialogOpen, setAddSubResellerDialogOpen] = useState(false);

  // Suspend request dialog state
  const [suspendDialog, setSuspendDialog] = useState({ open: false, type: '', id: '', name: '', reason: '' });
  const [submittingSuspend, setSubmittingSuspend] = useState(false);
  // Track pending suspend requests (by target_id)
  const [pendingSuspendIds, setPendingSuspendIds] = useState(new Set());
  
  // Quotation dialog state
  const [quotationDialogOpen, setQuotationDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [products, setProducts] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [creatingQuotation, setCreatingQuotation] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [newQuotation, setNewQuotation] = useState({
    client_id: '',
    product_id: '',
    billing_cycle: 'monthly',
    quantity: 1,
    unit_price: 0,
    validity_days: 30,
    notes: ''
  });
  
  const [newClientData, setNewClientData] = useState({
    email: '',
    password: '',
    company_name: '',
    gst_number: '',
    phone: '',
    address: ''
  });
  const [newSubResellerData, setNewSubResellerData] = useState({
    email: '',
    password: '',
    company_name: '',
    gst_number: '',
    phone: '',
    subdomain: '',
    joining_fee: 0
  });

  // Pending approvals state
  const [pendingClients, setPendingClients] = useState([]);
  const [pendingClientsLoading, setPendingClientsLoading] = useState(false);
  const [rejectDialog, setRejectDialog] = useState({ open: false, client: null, reason: '' });
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchClients();
    fetchPendingClients();
    fetchProductsAndPricing();
    fetchVisibleCategories();
    if (user?.role === 'reseller') {
      fetchSubResellers();
    }
  }, [user]);

  const fetchPendingClients = async () => {
    setPendingClientsLoading(true);
    try {
      const res = await axios.get(`${API}/partner/pending-clients`);
      setPendingClients(res.data || []);
    } catch { /* silently fail */ }
    finally { setPendingClientsLoading(false); }
  };

  const approvePendingClient = async (clientId) => {
    setProcessingId(clientId);
    try {
      await axios.put(`${API}/partner/pending-clients/${clientId}/approve`);
      toast.success('Client approved and account activated');
      fetchPendingClients();
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve client');
    } finally { setProcessingId(null); }
  };

  const rejectPendingClient = async () => {
    setProcessingId(rejectDialog.client?.id);
    try {
      await axios.put(`${API}/partner/pending-clients/${rejectDialog.client.id}/reject`, { reason: rejectDialog.reason });
      toast.success('Client registration rejected');
      setRejectDialog({ open: false, client: null, reason: '' });
      fetchPendingClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject client');
    } finally { setProcessingId(null); }
  };

  const fetchClients = async () => {
    try {
      const [clientsRes, pendingRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/reseller/pending-suspend-requests`).catch(() => ({ data: [] }))
      ]);
      setClients(clientsRes.data);
      setPendingSuspendIds(new Set(pendingRes.data.map(r => r.target_id)));
    } catch (error) {
      toast.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductsAndPricing = async () => {
    try {
      const [productsRes, pricingRes] = await Promise.all([
        axios.get(`${API}/reseller/visible-products`),
        axios.get(`${API}/reseller/pricing`)
      ]);
      setProducts(productsRes.data);
      setPricing(pricingRes.data);
    } catch (error) {
      console.log('Failed to fetch products/pricing');
    }
  };

  const fetchVisibleCategories = async () => {
    try {
      // Only fetch categories that have visible products
      const response = await axios.get(`${API}/reseller/visible-categories`);
      // Filter to only show categories with at least one visible product
      const categoriesWithProducts = response.data.filter(cat => cat.visible_product_count > 0);
      setCategories(categoriesWithProducts);
    } catch (error) {
      console.log('Failed to fetch categories');
    }
  };

  const fetchSubResellers = async () => {
    try {
      const [srRes, pendingRes] = await Promise.all([
        axios.get(`${API}/sub-resellers`),
        axios.get(`${API}/reseller/pending-suspend-requests`).catch(() => ({ data: [] }))
      ]);
      setSubResellers(srRes.data);
      setPendingSuspendIds(new Set(pendingRes.data.map(r => r.target_id)));
    } catch (error) {
      toast.error('Failed to fetch sub-resellers');
    }
  };

  const getProductPrice = (productId, billingCycle) => {
    const pricingItem = pricing.find(p => p.product?.id === productId);
    if (pricingItem?.custom_pricing?.custom_prices?.add?.[billingCycle]) {
      return pricingItem.custom_pricing.custom_prices.add[billingCycle];
    }
    // Check in filteredProducts first, then fall back to products
    const product = filteredProducts.find(p => p.id === productId) || products.find(p => p.id === productId);
    return product?.billing_prices?.[billingCycle] || product?.base_price || 0;
  };

  const openQuotationDialog = (client) => {
    setSelectedClient(client);
    setSelectedCategory('');
    setFilteredProducts([]);
    setNewQuotation({
      client_id: client.id,
      product_id: '',
      billing_cycle: 'monthly',
      quantity: 1,
      unit_price: 0,
      validity_days: 30,
      notes: ''
    });
    setQuotationDialogOpen(true);
  };

  const handleCategoryChange = async (categoryId) => {
    setSelectedCategory(categoryId);
    setNewQuotation(prev => ({
      ...prev,
      product_id: '',
      unit_price: 0
    }));
    
    if (categoryId) {
      try {
        // Fetch visible products filtered by category
        const response = await axios.get(`${API}/reseller/visible-products?category_id=${categoryId}`);
        setFilteredProducts(response.data);
      } catch (error) {
        console.log('Failed to fetch filtered products');
        setFilteredProducts([]);
      }
    } else {
      setFilteredProducts([]);
    }
  };

  const handleProductChange = (productId) => {
    const price = getProductPrice(productId, newQuotation.billing_cycle);
    setNewQuotation(prev => ({
      ...prev,
      product_id: productId,
      unit_price: price
    }));
  };

  const handleBillingCycleChange = (cycle) => {
    const price = getProductPrice(newQuotation.product_id, cycle);
    setNewQuotation(prev => ({
      ...prev,
      billing_cycle: cycle,
      unit_price: price,
      // Always reset validity_days to the natural period for the selected cycle
      validity_days: BILLING_CYCLE_MIN_DAYS[cycle] || 30,
    }));
  };

  const handleCreateQuotation = async () => {
    if (!newQuotation.product_id) {
      toast.error('Please select a product');
      return;
    }
    const minDays = BILLING_CYCLE_MIN_DAYS[newQuotation.billing_cycle] || 30;
    if ((newQuotation.validity_days || 0) < minDays) {
      toast.error(`Minimum validity for ${newQuotation.billing_cycle} is ${minDays} days`);
      return;
    }

    setCreatingQuotation(true);
    try {
      await axios.post(`${API}/quotations`, newQuotation);
      toast.success('Quotation created successfully');
      setQuotationDialogOpen(false);
      setSelectedClient(null);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = Array.isArray(errorDetail) 
        ? errorDetail.map(e => e.msg || e.message).join(', ')
        : (typeof errorDetail === 'string' ? errorDetail : 'Failed to create quotation');
      toast.error(errorMessage);
    } finally {
      setCreatingQuotation(false);
    }
  };

  const handleCreateClient = async () => {
    if (!newClientData.email || !newClientData.password || !newClientData.company_name) {
      toast.error('Please fill required fields');
      return;
    }
    const phoneErr = validateMobile(newClientData.phone);
    if (phoneErr) { toast.error(phoneErr); return; }
    const gstErr = validateGST(newClientData.gst_number);
    if (gstErr) { toast.error(gstErr); return; }

    try {
      await axios.post(`${API}/clients`, newClientData);
      toast.success('Client created successfully');
      setAddClientDialogOpen(false);
      setNewClientData({
        email: '',
        password: '',
        company_name: '',
        gst_number: '',
        phone: '',
        address: ''
      });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    }
  };

  const handleCreateSubReseller = async () => {
    if (!newSubResellerData.email || !newSubResellerData.password || !newSubResellerData.company_name) {
      toast.error('Please fill required fields');
      return;
    }
    const phoneErr = validateMobile(newSubResellerData.phone);
    if (phoneErr) { toast.error(phoneErr); return; }
    const gstErr = validateGST(newSubResellerData.gst_number);
    if (gstErr) { toast.error(gstErr); return; }

    try {
      await axios.post(`${API}/sub-resellers`, newSubResellerData);
      toast.success('Channel partner created successfully');
      setAddSubResellerDialogOpen(false);
      setNewSubResellerData({
        email: '',
        password: '',
        company_name: '',
        gst_number: '',
        phone: '',
        subdomain: '',
        joining_fee: 0
      });
      fetchSubResellers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create channel partner');
    }
  };

  // ── Suspend request flow ─────────────────────────────────────────────────
  const openSuspendDialog = (type, id, name) => {
    setSuspendDialog({ open: true, type, id, name, reason: '' });
  };

  const submitSuspendRequest = async () => {
    if (!suspendDialog.reason.trim()) {
      toast.error('Please provide a reason for the suspend request');
      return;
    }
    setSubmittingSuspend(true);
    try {
      const endpoint = user?.role === 'reseller'
        ? `${API}/reseller/suspend-request`
        : `${API}/sub-reseller/suspend-request`;
      await axios.post(endpoint, { target_id: suspendDialog.id, reason: suspendDialog.reason.trim() });
      toast.success('Suspend request submitted for Admin review');
      setSuspendDialog({ open: false, type: '', id: '', name: '', reason: '' });
      // Refresh both lists to pick up new pending status
      fetchClients();
      if (user?.role === 'reseller') fetchSubResellers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit suspend request');
    } finally {
      setSubmittingSuspend(false);
    }
  };

  // Client management actions
  const handleClientActivate = async (clientId) => {
    try {
      const endpoint = user?.role === 'reseller'
        ? `${API}/reseller/clients/${clientId}/activate`
        : `${API}/sub-reseller/clients/${clientId}/activate`;
      await axios.put(endpoint);
      toast.success('Client activated');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate client');
    }
  };

  const handleClientSuspend = async (clientId) => {
    try {
      const endpoint = user?.role === 'reseller'
        ? `${API}/reseller/clients/${clientId}/suspend`
        : `${API}/sub-reseller/clients/${clientId}/suspend`;
      await axios.put(endpoint);
      toast.success('Client suspended');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend client');
    }
  };

  const handleClientCancel = async (clientId) => {
    if (!window.confirm('Are you sure you want to cancel this client? This action cannot be undone.')) return;
    try {
      const endpoint = user?.role === 'reseller' 
        ? `${API}/reseller/clients/${clientId}/cancel`
        : `${API}/sub-reseller/clients/${clientId}/cancel`;
      await axios.put(endpoint);
      toast.success('Client cancelled');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel client');
    }
  };

  // Channel partner management actions (only for Synergy Partners)
  const handleSubResellerActivate = async (subResellerId) => {
    try {
      await axios.put(`${API}/reseller/sub-resellers/${subResellerId}/activate`);
      toast.success('Channel partner activated');
      fetchSubResellers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate channel partner');
    }
  };

  const handleSubResellerCancel = async (subResellerId) => {
    if (!window.confirm('Are you sure you want to cancel this channel partner? This action cannot be undone.')) return;
    try {
      await axios.put(`${API}/reseller/sub-resellers/${subResellerId}/cancel`);
      toast.success('Channel partner cancelled');
      fetchSubResellers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel channel partner');
    }
  };

  return (
    <DashboardLayout role={user?.role}>
      <div className="space-y-6">
        <div className="flex justify-between items-center flex-wrap md:nowrap mb-4">
          <div className="mr-0 sm:mr-4 mb-3">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="clients-page-title">
              {user?.role === 'reseller' ? 'Clients & Channel Partners' : 'Clients'}
            </h1>
            <p className="text-slate-500 mt-2">
              {user?.role === 'reseller' ? 'Manage your network' : 'Manage your clients'}
            </p>
          </div>
          <div className="flex-shrink-0 flex gap-2 mt-2 md:m-0">
            <Button
              onClick={fetchClients}
              variant="outline"
              className="border-slate-300 text-slate-600 hover:bg-slate-600 hover:border-slate-600"
              data-testid="refresh-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={() => {
                const ref = user?.account_id;
                const base = window.location.origin;
                const link = `${base}/client/signup?ref=${ref}`;
                navigator.clipboard.writeText(link).then(() => toast.success('Client referral link copied!'));
              }}
              variant="outline"
              className="border-blue-300 text-blue-700 hover:bg-blue-600 hover:border-blue-600"
              data-testid="copy-client-referral-btn"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Share Client Link
            </Button>
            {user?.role === 'reseller' && (
              <Button
                onClick={() => {
                  const ref = user?.account_id;
                  const base = window.location.origin;
                  const link = `${base}/signup?ref=${ref}&type=channel_partner`;
                  navigator.clipboard.writeText(link).then(() => toast.success('Channel Partner referral link copied!'));
                }}
                variant="outline"
                className="border-green-300 text-green-700 hover:bg-green-600 hover:border-green-600"
                data-testid="copy-cp-referral-btn"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share CP Link
              </Button>
            )}
          </div>
        </div>

        {/* Show tabs only for Synergy Partners (reseller role), show clients directly for Channel Partners (sub_reseller role) */}
        {user?.role === 'reseller' ? (
          <Tabs defaultValue="clients" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="clients">Clients ({clients.length})</TabsTrigger>
              <TabsTrigger value="pending">
                Pending Approvals
                {pendingClients.length > 0 && (
                  <span className="ml-1.5 bg-amber-500 text-white text-xs rounded-full px-1.5 py-0.5">{pendingClients.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="subresellers">Channel Partners ({subResellers.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="clients" className="mt-6">
              <Card data-testid="clients-table-card">
                <CardContent className="p-0">
                  {loading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                  ) : clients.length === 0 ? (
                    <div className="text-center py-16">
                      <p className="text-slate-500">No clients yet</p>
                      <p className="text-sm text-slate-400 mt-2">Share your referral link to invite clients</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-nowrap">Account ID</TableHead>
                          <TableHead className="text-nowrap">Company Name</TableHead>
                          <TableHead className="text-nowrap">Email</TableHead>
                          <TableHead className="text-nowrap">Phone</TableHead>
                          <TableHead className="text-nowrap">Status</TableHead>
                          <TableHead className="text-nowrap">Joined</TableHead>
                          <TableHead className="text-nowrap">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {clients.map((client) => (
                          <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                            <TableCell className="font-mono text-xs text-slate-500 text-nowrap">{client.account_id || '—'}</TableCell>
                            <TableCell className="font-medium text-nowrap">{client.company_name}</TableCell>                            
                            <TableCell className="text-nowrap">{client.email}</TableCell>
                            <TableCell className="text-nowrap">{client.phone || '-'}</TableCell>
                            <TableCell className="text-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                client.status === 'cancelled'
                                  ? 'bg-gray-100 text-gray-700'
                                  : client.is_active 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                              }`}>
                                {client.status === 'cancelled' ? 'Cancelled' : client.is_active ? 'Active' : 'Suspended'}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500 text-nowrap">
                              {new Date(client.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {client.status !== 'suspended' && client.is_active && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openQuotationDialog(client)}
                                  data-testid={`create-quotation-${client.id}`}
                                >
                                  <FileText className="h-4 w-4 mr-1" />
                                  Quote
                                </Button>
                                )}
                                {client.status !== 'cancelled' && (
                                  <>
                                    {client.is_active ? (
                                      pendingSuspendIds.has(client.id) ? (
                                        <span className="text-xs text-amber-600 font-medium px-2 py-1 bg-amber-50 rounded border border-amber-200" data-testid={`suspend-pending-${client.id}`}>
                                          Pending Admin
                                        </span>
                                      ) : (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="text-orange-600 border-orange-200 hover:bg-orange-600"
                                          onClick={() => openSuspendDialog('client', client.id, client.company_name || client.email)}
                                          data-testid={`suspend-client-${client.id}`}
                                          title="Request suspension (requires Admin approval)"
                                        >
                                          <Ban className="h-4 w-4" />
                                        </Button>
                                      )
                                    ) : client.status === 'suspended' ? (
                                      <span className="text-xs text-slate-500 px-2 py-1 bg-slate-50 rounded border" title="Only Admin can reactivate a suspended account">Admin only</span>
                                    ) : (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-green-600 border-green-200 hover:bg-green-50"
                                        onClick={() => handleClientActivate(client.id)}
                                        data-testid={`activate-client-${client.id}`}
                                      >
                                        <Check className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

          {/* Pending Approvals Tab */}
          <TabsContent value="pending" className="mt-6">
            <Card>
              <CardContent className="p-0">
                {pendingClientsLoading ? (
                  <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" /></div>
                ) : pendingClients.length === 0 ? (
                  <div className="text-center py-16">
                    <Check className="h-10 w-10 mx-auto text-slate-200 mb-3" />
                    <p className="text-slate-500 font-medium">No pending approvals</p>
                    <p className="text-sm text-slate-400 mt-1">Clients who sign up using your referral code will appear here</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name / Business</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Mobile</TableHead>
                        <TableHead>Account Type</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Signed Up</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingClients.map(client => (
                        <TableRow key={client.id} data-testid={`pending-client-${client.id}`}>
                          <TableCell>
                            <p className="font-medium text-slate-800 text-sm">{client.full_name || client.company_name}</p>
                            <p className="text-xs text-slate-500">{client.company_name}</p>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">{client.email}</TableCell>
                          <TableCell className="text-sm text-slate-600">{client.mobile_country_code} {client.mobile || client.phone}</TableCell>
                          <TableCell className="text-xs text-slate-500">{client.account_type || '—'}</TableCell>
                          <TableCell className="text-xs text-slate-500">
                            {[client.city, client.state_name].filter(Boolean).join(', ') || '—'}
                          </TableCell>
                          <TableCell className="text-xs text-slate-500 text-nowrap">
                            {new Date(client.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs h-7 px-2"
                                onClick={() => approvePendingClient(client.id)}
                                disabled={processingId === client.id}
                                data-testid={`approve-pending-${client.id}`}>
                                <Check className="h-3 w-3 mr-1" />Approve
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 text-xs h-7 px-2"
                                onClick={() => setRejectDialog({ open: true, client, reason: '' })}
                                disabled={processingId === client.id}
                                data-testid={`reject-pending-${client.id}`}>
                                <Ban className="h-3 w-3 mr-1" />Reject
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="subresellers" className="mt-6">
            <Card data-testid="subresellers-table-card">
              <CardContent className="p-0">
                {subResellers.length === 0 ? (
                  <div className="text-center py-16">
                    <p className="text-slate-500">No channel partners yet</p>
                    <p className="text-sm text-slate-400 mt-2">Share your CP referral link to invite channel partners</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-nowrap">Company Name</TableHead>
                        <TableHead className="text-nowrap">Email</TableHead>
                        <TableHead className="text-nowrap">Subdomain</TableHead>
                        <TableHead className="text-nowrap">Status</TableHead>
                        <TableHead className="text-nowrap">Joined</TableHead>
                        <TableHead className="text-nowrap">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subResellers.map((subReseller) => (
                        <TableRow key={subReseller.id} data-testid={`subreseller-row-${subReseller.id}`}>
                          <TableCell className="font-medium">{subReseller.company_name}</TableCell>
                          <TableCell>{subReseller.email}</TableCell>
                          <TableCell className="mono text-sm">{subReseller.subdomain || '-'}</TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              subReseller.status === 'cancelled'
                                ? 'bg-gray-100 text-gray-700'
                                : subReseller.is_active 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                            }`}>
                              {subReseller.status === 'cancelled' ? 'Cancelled' : subReseller.is_active ? 'Active' : 'Suspended'}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {new Date(subReseller.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {subReseller.status !== 'cancelled' && (
                                <>
                                  {subReseller.is_active ? (
                                    pendingSuspendIds.has(subReseller.id) ? (
                                      <span className="text-xs text-amber-600 font-medium px-2 py-1 bg-amber-50 rounded border border-amber-200" data-testid={`suspend-pending-${subReseller.id}`}>
                                        Pending Admin
                                      </span>
                                    ) : (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-orange-600 border-orange-200 hover:bg-orange-600"
                                        onClick={() => openSuspendDialog('subreseller', subReseller.id, subReseller.company_name || subReseller.email)}
                                        data-testid={`suspend-subreseller-${subReseller.id}`}
                                        title="Request suspension (requires Admin approval)"
                                      >
                                        <Ban className="h-4 w-4" />
                                      </Button>
                                    )
                                  ) : subReseller.status === 'suspended' ? (
                                    <span className="text-xs text-slate-500 px-2 py-1 bg-slate-50 rounded border" title="Only Admin can reactivate a suspended account">Admin only</span>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-green-600 border-green-200 hover:bg-green-50"
                                      onClick={() => handleSubResellerActivate(subReseller.id)}
                                      data-testid={`activate-subreseller-${subReseller.id}`}
                                    >
                                      <Check className="h-4 w-4" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        ) : (
          /* Channel Partner (sub_reseller) - Show clients directly without tabs */
          <Card data-testid="clients-table-card">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : clients.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-slate-500">No clients yet</p>
                  <p className="text-sm text-slate-400 mt-2">Share your referral link to invite clients</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Company Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.map((client) => (
                      <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                        <TableCell className="font-medium">{client.company_name}</TableCell>
                        <TableCell>{client.email}</TableCell>
                        <TableCell>{client.phone || '-'}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            client.status === 'cancelled'
                              ? 'bg-gray-100 text-gray-700'
                              : client.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                          }`}>
                            {client.status === 'cancelled' ? 'Cancelled' : client.is_active ? 'Active' : 'Suspended'}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {new Date(client.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {client.status !== 'suspended' && client.is_active && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openQuotationDialog(client)}
                              data-testid={`create-quotation-${client.id}`}
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Quote
                            </Button>
                            )}
                            {client.status !== 'cancelled' && (
                              <>
                                {client.is_active ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-orange-600 border-orange-200 hover:bg-orange-50"
                                    onClick={() => handleClientSuspend(client.id)}
                                    data-testid={`suspend-client-${client.id}`}
                                  >
                                    <Ban className="h-4 w-4" />
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-600 border-green-200 hover:bg-green-50"
                                    onClick={() => handleClientActivate(client.id)}
                                    data-testid={`activate-client-${client.id}`}
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Add Client and Add Channel Partner dialogs removed — use referral links instead */}

        {/* Create Quotation Dialog */}
        <Dialog open={quotationDialogOpen} onOpenChange={setQuotationDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Quotation for {selectedClient?.company_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="bg-[#115ab4]/10 p-3 rounded-lg text-sm">
                <p><span className="text-slate-500">Client:</span> {selectedClient?.company_name}</p>
                <p><span className="text-slate-500">Email:</span> {selectedClient?.email}</p>
              </div>

              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select a category first" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(category => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name} ({category.visible_product_count} products)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {categories.length === 0 && (
                  <p className="text-xs text-amber-600">No categories with visible products available</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Product *</Label>
                <Select 
                  value={newQuotation.product_id} 
                  onValueChange={handleProductChange}
                  disabled={!selectedCategory}
                >
                  <SelectTrigger data-testid="product-select">
                    <SelectValue placeholder={selectedCategory ? "Select a product" : "Select a category first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredProducts.map(product => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedCategory && filteredProducts.length === 0 && (
                  <p className="text-xs text-amber-600">No visible products in this category</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Billing Cycle</Label>
                  <Select value={newQuotation.billing_cycle} onValueChange={handleBillingCycleChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BILLING_CYCLES.map(cycle => (
                        <SelectItem key={cycle.key} value={cycle.key}>
                          {cycle.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={newQuotation.quantity}
                    onChange={(e) => setNewQuotation(prev => ({...prev, quantity: parseInt(e.target.value) || 1}))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Unit Price (₹)</Label>
                  <Input
                    type="number"
                    value={newQuotation.unit_price}
                    readOnly
                    disabled
                    className="bg-slate-100"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Valid for (days)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={newQuotation.validity_days}
                    onChange={(e) => {
                      const v = parseInt(e.target.value);
                      if (!isNaN(v)) setNewQuotation(prev => ({...prev, validity_days: v}));
                    }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes/Remarks</Label>
                <Textarea
                  placeholder="Add any notes..."
                  value={newQuotation.notes}
                  onChange={(e) => setNewQuotation(prev => ({...prev, notes: e.target.value}))}
                />
              </div>

              <div className="bg-[#115ab4]/10 p-4 rounded-lg space-y-1.5 text-sm">
                {(() => {
                  const subTotal = (newQuotation.unit_price || 0) * (newQuotation.quantity || 1);
                  const gstAmt   = Math.round(subTotal * 0.18 * 100) / 100;
                  const total    = Math.round((subTotal + gstAmt) * 100) / 100;
                  const clientGstType = determineGstType(getEntityGstin(selectedClient), getEntityState(selectedClient));
                  const half = Math.round(gstAmt / 2 * 100) / 100;
                  const entityState = getEntityState(selectedClient);
                  const entityGstin = getEntityGstin(selectedClient);
                  return (
                    <>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Sub Total</span>
                        <span className="font-medium">₹{subTotal.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                      </div>
                      {clientGstType === 'CGST+SGST' ? (
                        <>
                          <div className="flex justify-between">
                            <span className="text-slate-500">CGST (9.00%)</span>
                            <span className="font-medium">₹{half.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">SGST (9.00%)</span>
                            <span className="font-medium">₹{half.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                          </div>
                        </>
                      ) : (
                        <div className="flex justify-between">
                          <span className="text-slate-500">IGST (18.00%)</span>
                          <span className="font-medium">₹{gstAmt.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                        </div>
                      )}
                      <div className="flex justify-between border-t border-blue-200 pt-1.5 font-semibold">
                        <span>Total</span>
                        <span className="text-blue-700">₹{total.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setQuotationDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleCreateQuotation} 
                disabled={creatingQuotation || !newQuotation.product_id}
                data-testid="create-quotation-submit"
              >
                {creatingQuotation ? 'Creating...' : 'Create Quotation'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Suspend Request Dialog ── */}
        <Dialog open={suspendDialog.open} onOpenChange={open => !open && setSuspendDialog(d => ({ ...d, open: false }))}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-orange-700">
                <AlertTriangle className="h-5 w-5" />
                Request Account Suspension
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                You are requesting to suspend <strong>{suspendDialog.name}</strong>. This will be sent to Admin for approval.
              </div>
              <div className="space-y-2">
                <Label>Reason for Suspension <span className="text-red-500">*</span></Label>
                <Textarea
                  placeholder="Describe why this account should be suspended..."
                  rows={4}
                  value={suspendDialog.reason}
                  onChange={e => setSuspendDialog(d => ({ ...d, reason: e.target.value }))}
                  data-testid="suspend-reason-input"
                />
                {suspendDialog.reason.trim() === '' && (
                  <p className="text-xs text-red-500">A reason is required to submit this request.</p>
                )}
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setSuspendDialog({ open: false, type: '', id: '', name: '', reason: '' })}>
                Cancel
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                disabled={!suspendDialog.reason.trim() || submittingSuspend}
                onClick={submitSuspendRequest}
                data-testid="submit-suspend-request"
              >
                {submittingSuspend ? 'Submitting...' : 'Submit Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Pending Client Dialog */}
        <Dialog open={rejectDialog.open} onOpenChange={open => !open && setRejectDialog({ open: false, client: null, reason: '' })}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-700">Reject Client Registration</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Rejecting <strong>{rejectDialog.client?.full_name || rejectDialog.client?.company_name}</strong>'s registration will prevent them from logging in.
              </p>
              <div>
                <Label className="text-xs font-medium text-slate-600 mb-1 block">Reason (optional)</Label>
                <Textarea
                  placeholder="Why are you rejecting this registration?"
                  value={rejectDialog.reason}
                  onChange={e => setRejectDialog(d => ({ ...d, reason: e.target.value }))}
                  className="min-h-[80px] resize-none"
                  data-testid="reject-pending-reason"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setRejectDialog({ open: false, client: null, reason: '' })}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={rejectPendingClient}
                disabled={processingId === rejectDialog.client?.id} data-testid="confirm-reject-pending">
                Reject Registration
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
